import { type DateTime } from "luxon";

import { baseRoutes } from "@/api/base-routes";
import { isValidReportingParams } from "@/hooks/reporting";
import {
	DEFAULT_CARTON_CLOSING_API_RESPONSE,
	type CartonClosingApiResponse,
	type PlannedProductionData,
	type ReportingQueryParams,
} from "@/schemas";
import { constructUrl, constructUrlFromDateTime, fetchData, type ApiRequestBody } from "@/api/reporting-api";
import type { MachineSelection } from "@/views/reporting/types";

export const getCartonClosingBuckets = async (
	params: ReportingQueryParams,
	signal?: AbortSignal,
): Promise<CartonClosingApiResponse> => {
	const { schedules, sites, machinesSelected, dateRange, timeZoneId } = params;

	if (!isValidReportingParams(params) || !machinesSelected?.length) {
		return DEFAULT_CARTON_CLOSING_API_RESPONSE;
	}

	const endpoint = `${baseRoutes.reporting}/v1/CartonProduction/CartonClosingBuckets`;
	const url = constructUrl(endpoint, dateRange, timeZoneId);
	const body: ApiRequestBody = { schedules, sites, machines: machinesSelected };

	const results = await fetchData<CartonClosingApiResponse>(url, body, signal);
	return results ?? DEFAULT_CARTON_CLOSING_API_RESPONSE;
};

export const hasCartonClosingBuckets = async (signal?: AbortSignal): Promise<boolean> => {
	const endpoint = `${baseRoutes.reporting}/v1/CartonProduction/HasCartonClosingBuckets`;
	const result = await fetchData<boolean>(endpoint, { schedules: [], sites: [] }, signal);
	return result ?? false;
};

export async function getClosingMachinesInTimeRange(
	schedules: PlannedProductionData[],
	sites: string[],
	startDate: DateTime,
	endDate: DateTime,
	timeZoneId: string,
	signal?: AbortSignal,
): Promise<MachineSelection[]> {
	if (!sites.length || !startDate?.isValid || !endDate?.isValid || startDate.equals(endDate)) {
		return [];
	}

	const endpoint = `${baseRoutes.reporting}/v1/MachineSelection/ClosingMachinesInTimeRange`;
	const url = constructUrlFromDateTime(endpoint, startDate, endDate, timeZoneId);
	const body: ApiRequestBody = { schedules, sites };

	const machines = await fetchData<MachineSelection[]>(url, body, signal);
	return machines ?? [];
}
